import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  GDSPhysicoChemicalIndicator,
  GDSPhysicoChemicalIndicatorCreateResponse,
  GDSPhysicoChemicalIndicatorForm,
  Indicator,
} from './types';

export const gdsPhysicoChemicalIndicatorApi = createApi({
  reducerPath: 'gdsPhysicoChemicalIndicatorApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/gds-physico-chemical-indicators`,
  }),
  tagTypes: ['GDSPhysicoChemicalIndicators', 'GDSPhysicoChemicalIndicator'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getGDSPhysicoChemicalIndicator: builder.infiniteQuery<
      GDSPhysicoChemicalIndicator,
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['GDSPhysicoChemicalIndicators'],
    }),
    getGDSPhysicoChemicalIndicatorById: builder.query<Indicator, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: result => [
        { type: 'GDSPhysicoChemicalIndicator', id: result?.id },
      ],
    }),
    createGDSPhysicoChemicalIndicator: builder.mutation<
      GDSPhysicoChemicalIndicatorCreateResponse,
      GDSPhysicoChemicalIndicatorForm
    >({
      query: data => ({
        url: ``,
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'GDSPhysicoChemicalIndicators',
        { type: 'GDSPhysicoChemicalIndicator', id: result?.id },
      ],
    }),
    updateGDSPhysicoChemicalIndicatorById: builder.mutation<
      string,
      GDSPhysicoChemicalIndicatorForm
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'GDSPhysicoChemicalIndicators',
        { type: 'GDSPhysicoChemicalIndicator', id: result },
      ],
    }),
  }),
});

export const {
  useGetGDSPhysicoChemicalIndicatorByIdQuery,
  useCreateGDSPhysicoChemicalIndicatorMutation,
  useUpdateGDSPhysicoChemicalIndicatorByIdMutation,
} = gdsPhysicoChemicalIndicatorApi;

const normalizeInfiniteQueryData =
  (hook: any) => (params?: any, options?: any) => {
    const payload = hook(params, options);

    return {
      ...payload,
      data: (payload.data?.pages || []).flat().reduce((acc, page) => {
        return {
          ...acc,
          ...page,
          indicators: (acc?.indicators || []).concat(page.indicators),
        };
      }, {}),
    };
  };

export const useGetGDSPhysicoChemicalIndicatorInfiniteQuery =
  normalizeInfiniteQueryData(
    gdsPhysicoChemicalIndicatorApi.useGetGDSPhysicoChemicalIndicatorInfiniteQuery,
  );
